import { create } from 'zustand';
import type { ConversionFile, ImageFormat } from '@/types';

interface AppState {
  files: ConversionFile[];
  isConverting: boolean;
  globalFormat: ImageFormat;
  globalQuality: number;
  
  // Actions
  addFiles: (files: File[]) => void;
  removeFile: (id: string) => void;
  clearFiles: () => void;
  updateFile: (id: string, updates: Partial<ConversionFile>) => void;
  updateFileStatus: (id: string, status: ConversionFile['status'], progress?: number) => void;
  setFileResult: (id: string, result: ConversionFile['result']) => void;
  setFileError: (id: string, error: string) => void;
  setGlobalFormat: (format: ImageFormat) => void;
  setGlobalQuality: (quality: number) => void;
  setIsConverting: (isConverting: boolean) => void;
  applyGlobalSettings: () => void;
}

import { generateFileId } from '@/types';

export const useAppStore = create<AppState>((set, get) => ({
  files: [],
  isConverting: false,
  globalFormat: 'webp',
  globalQuality: 85,

  addFiles: (newFiles) => {
    const conversionFiles: ConversionFile[] = newFiles.map((file) => ({
      id: generateFileId(),
      file,
      name: file.name,
      size: file.size,
      type: file.type,
      status: 'pending',
      progress: 0,
      targetFormat: get().globalFormat,
      quality: get().globalQuality,
    }));

    set((state) => ({
      files: [...state.files, ...conversionFiles],
    }));

    // 异步生成预览
    conversionFiles.forEach(async (cf) => {
      const { ImageConverter } = await import('@/lib/imageConverter');
      const preview = await ImageConverter.getPreview(cf.file);
      get().updateFile(cf.id, { preview });
    });
  },

  removeFile: (id) => {
    set((state) => {
      const file = state.files.find((f) => f.id === id);
      if (file?.result?.url) {
        URL.revokeObjectURL(file.result.url);
      }
      return {
        files: state.files.filter((f) => f.id !== id),
      };
    });
  },

  clearFiles: () => {
    set((state) => {
      // 清理所有 blob URL
      state.files.forEach((file) => {
        if (file.result?.url) {
          URL.revokeObjectURL(file.result.url);
        }
      });
      return { files: [] };
    });
  },

  updateFile: (id, updates) => {
    set((state) => ({
      files: state.files.map((f) =>
        f.id === id ? { ...f, ...updates } : f
      ),
    }));
  },

  updateFileStatus: (id, status, progress) => {
    set((state) => ({
      files: state.files.map((f) =>
        f.id === id ? { ...f, status, progress: progress ?? f.progress } : f
      ),
    }));
  },

  setFileResult: (id, result) => {
    set((state) => ({
      files: state.files.map((f) =>
        f.id === id ? { ...f, result, status: 'completed', progress: 100 } : f
      ),
    }));
  },

  setFileError: (id, error) => {
    set((state) => ({
      files: state.files.map((f) =>
        f.id === id ? { ...f, error, status: 'error', progress: 0 } : f
      ),
    }));
  },

  setGlobalFormat: (format) => set({ globalFormat: format }),
  
  setGlobalQuality: (quality) => set({ globalQuality: quality }),
  
  setIsConverting: (isConverting) => set({ isConverting }),

  applyGlobalSettings: () => {
    const { globalFormat, globalQuality } = get();
    set((state) => ({
      files: state.files.map((f) =>
        f.status === 'pending'
          ? { ...f, targetFormat: globalFormat, quality: globalQuality }
          : f
      ),
    }));
  },
}));